import React, { useState, forwardRef, useImperativeHandle } from 'react';
import { toast } from 'react-toastify';
import { patientAPI, queueAPI } from '../../utils/api';
import { printPatientSlip } from '../../utils/printSlip';

const DEPT_TOKEN_MAP = { OPD: 'OPD', Laboratory: 'LAB', Radiology: 'RAD', Psychiatry: 'PSY', LAB: 'LAB', RAD: 'RAD', PSY: 'PSY' };

const inputStyle = { width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 13, boxSizing: 'border-box' };

// Auto-format CNIC: 42301-1234567-8
const formatCnic = (val) => {
    const digits = val.replace(/\D/g, '').slice(0, 13);
    if (digits.length <= 5) return digits;
    if (digits.length <= 12) return `${digits.slice(0, 5)}-${digits.slice(5)}`;
    return `${digits.slice(0, 5)}-${digits.slice(5, 12)}-${digits.slice(12)}`;
};

// Auto-format Phone: 0300-1234567
const formatPhone = (val) => {
    const digits = val.replace(/\D/g, '').slice(0, 11);
    if (digits.length <= 4) return digits;
    return `${digits.slice(0, 4)}-${digits.slice(4)}`;
};

const RELATIONS = ['S/O', 'D/O', 'W/O', 'B/O', 'F/O', 'M/O'];

const CITIES = {
    Pakistan: ['Lahore','Faisalabad','Rawalpindi','Islamabad','Gujranwala','Multan','Bahawalpur','Sargodha','Sialkot','Sheikhupura','Jhang','Rahim Yar Khan','Gujrat','Sahiwal','Okara','Kasur','Hafizabad','Khanewal','Jhelum','Attock','Chakwal','Vehari','Muzaffargarh','Narowal','Mianwali','Dera Ghazi Khan','Wazirabad','Karachi','Hyderabad','Sukkur','Larkana','Nawabshah','Mirpur Khas','Jacobabad','Shikarpur','Khairpur','Badin','Sanghar','Thatta','Ghotki','Peshawar','Mardan','Mingora','Kohat','Abbottabad','Mansehra','Dera Ismail Khan','Bannu','Nowshera','Charsadda','Swabi','Haripur','Quetta','Turbat','Khuzdar','Hub','Chaman','Gwadar','Zhob','Kalat','Pishin','Muzaffarabad','Mirpur (AJK)','Rawalakot','Gilgit','Skardu','Hunza'].sort(),
    Iran: ['Tehran','Mashhad','Isfahan','Karaj','Tabriz','Shiraz','Ahvaz','Qom','Kermanshah','Zahedan','Rasht','Kerman'],
    Afghanistan: ['Kabul','Kandahar','Herat','Mazar-i-Sharif','Jalalabad','Kunduz','Lashkar Gah','Khost','Ghazni'],
    UAE: ['Dubai','Abu Dhabi','Sharjah','Ajman','Ras Al Khaimah','Fujairah','Al Ain'],
    'Saudi Arabia': ['Riyadh','Jeddah','Mecca','Medina','Dammam','Khobar','Tabuk','Abha','Taif','Jubail','Yanbu'],
    Qatar: ['Doha','Al Wakrah','Al Khor','Al Rayyan'],
    Kuwait: ['Kuwait City','Hawalli','Salmiya','Farwaniya','Ahmadi','Jahra'],
    Other: [],
};
const COUNTRIES = Object.keys(CITIES);

const PatientLookup = forwardRef(function PatientLookup({ onPatientReady, department = '', getVisitDetails, hideButton = false, onAfterRegister = null, onReprint = null }, ref) {
    const [query, setQuery] = useState('');
    const [checking, setChecking] = useState(false);
    const [found, setFound] = useState(null);
    const [form, setForm] = useState({ full_name: '', phone: '', gender: '', age_years: '', name_relation: 'S/O', father_name: '', cnic: '', country: 'Pakistan', city: '' });
    const [registering, setRegistering] = useState(false);

    const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));

    const handleQueryChange = (e) => {
        setQuery(e.target.value);
    };

    const search = async () => {
        if (!query.trim()) return;
        setChecking(true);
        try {
            const res = await patientAPI.search(query.trim());
            const patients = res.data.patients || [];
            const q = query.trim();
            // Match by MRN, CNIC, phone, or name
            const match = patients.find(p =>
                p.mrn === q ||
                p.cnic === q ||
                p.phone === q ||
                p.full_name?.toLowerCase() === q.toLowerCase()
            ) || patients[0];

            if (match) {
                setFound(match);
                onPatientReady(match);
                toast.success(`Patient found — ${match.full_name} (${match.mrn})`);
            } else {
                toast.info('Patient not found — please fill in details below');
            }
        } catch {
            toast.error('Search failed');
        } finally { setChecking(false); }
    };

    const registerNew = async () => {
        if (!form.full_name || !form.gender || !form.phone) {
            return toast.error('Name, Gender and Phone are required');
        }
        setRegistering(true);
        try {
            const res = await patientAPI.register({ ...form, nationality: 'Pakistani' });
            const patient = res.data.patient;
            setFound(patient);
            onPatientReady(patient);
            toast.success(`Registered! MRN: ${patient.mrn}`);
            let tokenNumber = null;
            const deptKey = DEPT_TOKEN_MAP[department];
            if (deptKey) {
                try {
                    const tRes = await queueAPI.issueToken({ department: deptKey, patient_name: patient.full_name, patient_id: patient.id });
                    tokenNumber = tRes.data.token.token_number;
                    toast.info(`Queue Token: ${tokenNumber}`);
                } catch {}
            }
            if (!hideButton) printPatientSlip(patient, department, getVisitDetails ? getVisitDetails() : {}, tokenNumber);
            if (onAfterRegister) onAfterRegister(patient);
        } catch (err) {
            if (err.response?.status === 409) {
                toast.warning(`Already registered — MRN: ${err.response.data.existing_mrn}`);
                const res2 = await patientAPI.search(form.cnic || form.phone);
                const match = res2.data.patients?.[0];
                if (match) { setFound(match); onPatientReady(match); if (onAfterRegister) onAfterRegister(match); }
            } else {
                toast.error(err.response?.data?.message || 'Registration failed');
            }
        } finally { setRegistering(false); }
    };

    useImperativeHandle(ref, () => ({ triggerRegister: registerNew }));

    const reset = () => {
        setQuery(''); setFound(null);
        setForm({ full_name: '', phone: '', gender: '', age_years: '', name_relation: 'S/O', father_name: '', cnic: '', country: 'Pakistan', city: '' });
        onPatientReady(null);
    };

    if (found) {
        return (
            <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 10, padding: 14, marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                    <div style={{ fontWeight: 700, color: '#166534', fontSize: 14 }}>✅ {found.full_name}</div>
                    <div style={{ fontSize: 12, color: '#166534', marginTop: 2 }}>
                        MRN: <strong>{found.mrn}</strong> | {found.gender} | Age: {found.age_years || '?'} {found.cnic ? `| CNIC: ${found.cnic}` : ''}
                    </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-end' }}>
                    <button onClick={() => onReprint ? onReprint(found) : printPatientSlip(found, department, getVisitDetails ? getVisitDetails() : {})}
                        style={{ color: 'white', background: '#0369a1', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 11, fontWeight: 600, padding: '5px 10px' }}>🖨️ Reprint</button>
                    <button onClick={reset}
                        style={{ color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>✕ Change</button>
                </div>
            </div>
        );
    }

    return (
        <div style={{ background: 'white', borderRadius: 10, padding: 20, border: '1px solid #e2e8f0', marginBottom: 16 }}>
            <h3 style={{ fontSize: 13, fontWeight: 700, color: '#1e3a5f', marginBottom: 12 }}>Patient Details</h3>

            {/* Single Search Box */}
            <div style={{ background: '#f8fafc', borderRadius: 8, padding: '10px 14px', marginBottom: 14, border: '1px solid #e2e8f0' }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 6 }}>
                    Existing Patient Search — MRN, CNIC, Phone or Name (optional)
                </label>
                <div style={{ display: 'flex', gap: 8 }}>
                    <input style={{ ...inputStyle, flex: 1, background: 'white' }}
                        value={query}
                        onChange={handleQueryChange}
                        onKeyDown={e => e.key === 'Enter' && search()}
                        placeholder="MRN-00001  |  42301-1234567-8  |  0300-1234567  |  Patient Name" />
                    <button onClick={search} disabled={checking || !query.trim()}
                        style={{ padding: '9px 20px', background: checking ? '#9ca3af' : '#1e3a5f', color: 'white', border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}>
                        {checking ? '...' : '🔍 Find'}
                    </button>
                </div>
            </div>

            {/* Registration form */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div>
                    <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Full Name *</label>
                    <input style={inputStyle} value={form.full_name} onChange={e => setF('full_name', e.target.value)} placeholder="Patient full name" />
                </div>
                <div>
                    <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Relation / Guardian Name</label>
                    <div style={{ display: 'flex', gap: 6 }}>
                        <select style={{ ...inputStyle, width: 90, flexShrink: 0, background: 'white' }} value={form.name_relation} onChange={e => setF('name_relation', e.target.value)}>
                            {RELATIONS.map(r => <option key={r}>{r}</option>)}
                        </select>
                        <input style={{ ...inputStyle, flex: 1 }} value={form.father_name} onChange={e => setF('father_name', e.target.value)} placeholder="Guardian name" />
                    </div>
                </div>
                <div>
                    <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Gender *</label>
                    <select style={{ ...inputStyle, background: 'white' }} value={form.gender} onChange={e => setF('gender', e.target.value)}>
                        <option value="">Select</option>
                        <option>Male</option><option>Female</option><option>Other</option>
                    </select>
                </div>
                <div>
                    <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Age (Years)</label>
                    <input type="number" style={inputStyle} value={form.age_years} onChange={e => setF('age_years', e.target.value)} placeholder="e.g. 35" min={0} max={150} />
                </div>
                <div>
                    <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Phone *</label>
                    <input style={inputStyle} value={form.phone} onChange={e => setF('phone', formatPhone(e.target.value))} placeholder="0300-1234567" maxLength={12} />
                </div>
                <div>
                    <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>CNIC</label>
                    <input style={inputStyle} value={form.cnic} onChange={e => setF('cnic', formatCnic(e.target.value))} placeholder="42301-1234567-8" maxLength={15} />
                </div>
                <div>
                    <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Country</label>
                    <select style={{ ...inputStyle, background: 'white' }} value={form.country} onChange={e => setF('country', e.target.value) || setF('city', '')}>
                        {COUNTRIES.map(c => <option key={c}>{c}</option>)}
                    </select>
                </div>
                <div>
                    <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>City</label>
                    <select style={{ ...inputStyle, background: 'white' }} value={form.city} onChange={e => setF('city', e.target.value)}>
                        <option value="">— Select City —</option>
                        {(CITIES[form.country] || []).map(c => <option key={c}>{c}</option>)}
                        <option value="Other">Other</option>
                    </select>
                </div>
            </div>

            {!hideButton && (
                <button onClick={registerNew} disabled={registering}
                    style={{ marginTop: 14, padding: '9px 24px', background: registering ? '#9ca3af' : '#059669', color: 'white', border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                    {registering ? 'Registering...' : '✅ Confirm Patient & Continue'}
                </button>
            )}
        </div>
    );
});

export default PatientLookup;
